import React, { useMemo, useState } from 'react';
import PublicHeader from '../../components/layout/PublicHeader';
import PublicFooter from '../../components/layout/PublicFooter';
import PublicCtaSection from '../../components/layout/PublicCtaSection';
import BlogFeaturedCard from '../../components/blog/BlogFeaturedCard';
import BlogGuideCard from '../../components/blog/BlogGuideCard';
import BlogGuideFilters from '../../components/blog/BlogGuideFilters';
import SEO from '../../components/seo/SEO';
import { useI18n } from '../../context/I18nContext';
import { getAllPosts } from '../../lib/blog';
import { buildBreadcrumbSchema } from '../../lib/breadcrumbSchema';

function formatTagLabel(tag: string): string {
  if (!tag) return tag;
  return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
}

const BlogPage: React.FC = () => {
  const { t, tSeo, language } = useI18n();
  const lang = language === 'fr' ? 'fr' : 'en';
  const posts = getAllPosts(lang);
  const [activeFilter, setActiveFilter] = useState<string>('all');

  const featured = posts[0];
  const remaining = useMemo(() => posts.slice(1), [posts]);

  const filterTags = useMemo(() => {
    const counts = new Map<string, number>();
    for (const post of remaining) {
      for (const tag of post.tags) {
        const key = tag.trim().toLowerCase();
        if (!key) continue;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([tag]) => tag);
  }, [remaining]);

  const filteredGuides = useMemo(() => {
    if (activeFilter === 'all') return remaining;
    return remaining.filter((post) =>
      post.tags.some((tag) => tag.trim().toLowerCase() === activeFilter)
    );
  }, [remaining, activeFilter]);

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: tSeo('landing.home'), url: 'https://leadsme.app/' },
    { name: tSeo('blog.nav'), url: 'https://leadsme.app/blog' },
  ]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <SEO
        title={tSeo('blog.seoTitle')}
        description={tSeo('blog.seoDescription')}
        canonical="https://leadsme.app/blog"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <PublicHeader />

      <main className="flex-1 pt-16">
        <section className="relative overflow-hidden pb-10 sm:pb-14">
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(59,130,246,0.12),_transparent_55%),radial-gradient(ellipse_at_top_right,_rgba(34,211,238,0.08),_transparent_40%)]"
            aria-hidden
          />

          <div className="relative mx-auto max-w-6xl px-4 pt-8 sm:px-6 sm:pt-10 lg:px-8">
            <div className="mb-8 text-left sm:mb-10">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-blue-600 sm:text-sm">
                {t('blog.mediaEyebrow')}
              </p>
              <h1 className="mb-3 max-w-4xl text-2xl font-bold tracking-tight text-slate-900 sm:mb-4 sm:text-3xl md:text-4xl md:leading-[1.2]">
                {t('blog.heroTitle')}
              </h1>
              <p className="max-w-3xl text-sm leading-relaxed text-slate-600 sm:text-base">
                {t('blog.heroSubtitle')}
              </p>
            </div>

            {featured ? (
              <BlogFeaturedCard post={featured} lang={lang} />
            ) : (
              <p className="py-16 text-center text-slate-400">{t('blog.empty')}</p>
            )}
          </div>
        </section>

        {remaining.length > 0 && (
          <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6 sm:pb-14 lg:px-8">
            <BlogGuideFilters
              title={t('blog.allGuides')}
              depsKey={`${lang}|${filterTags.join('|')}|${t('blog.filterAll')}`}
            >
              <button
                type="button"
                onClick={() => setActiveFilter('all')}
                className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-base font-medium transition-colors sm:px-3.5 sm:text-sm ${
                  activeFilter === 'all'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-200 hover:text-blue-600'
                }`}
              >
                {t('blog.filterAll')}
              </button>
              {filterTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveFilter(tag)}
                  className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-base font-medium transition-colors sm:px-3.5 sm:text-sm ${
                    activeFilter === tag
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-blue-200 hover:text-blue-600'
                  }`}
                >
                  {formatTagLabel(tag)}
                </button>
              ))}
            </BlogGuideFilters>

            {filteredGuides.length === 0 ? (
              <p className="py-12 text-center text-slate-400">{t('blog.emptyFilter')}</p>
            ) : (
              <div className="grid gap-3 sm:gap-4 md:grid-cols-2 md:gap-5">
                {filteredGuides.map((post) => (
                  <BlogGuideCard key={post.slug} post={post} lang={lang} />
                ))}
              </div>
            )}
          </section>
        )}

        <PublicCtaSection />
      </main>

      <PublicFooter />
    </div>
  );
};

export default BlogPage;
